import { api } from './client';
import type { Responsavel } from './distribuicao';

/** Regras de distribuição do escritório (docs/PLANO_REGRAS_DISTRIBUICAO.md). */

export type CampoRegra =
  | 'CLIENTE'
  | 'PARTE_DOCUMENTO'
  | 'PARTE_NOME'
  | 'POLO_DO_CLIENTE'
  | 'CLASSE'
  | 'ASSUNTO'
  | 'VALOR'
  | 'TRIBUNAL'
  | 'JUSTICA'
  | 'UF'
  | 'ORGAO'
  | 'MUNICIPIO'
  | 'ORIGEM'
  | 'DONO'
  /** Critério em texto livre, avaliado pela IA na capa (só sugere). */
  | 'IA';

export type PoloRegra = 'AUTOR' | 'REU';

/** Uma condição: casa se o processo tem QUALQUER um dos valores (OU). Entre condições, E. */
export interface CondicaoRegra {
  campo: CampoRegra;
  valores: string[];
  /** Só nas condições de parte: a parte deste polo. */
  polo?: PoloRegra | null;
  /** Só na de valor: a faixa (um dos dois basta). */
  minimo?: string | null;
  maximo?: string | null;
  /** Só para a tela: o nome de cada valor que é código ou id. */
  rotulos?: Record<string, string>;
}

export type ModoRegra = 'SUGERIR' | 'APLICAR';

export interface RegraDistribuicao {
  id: string;
  nome: string;
  ordem: number;
  ativa: boolean;
  modo: ModoRegra;
  condicoes: CondicaoRegra[];
  destino: Responsavel;
  /** O destino deixou de valer (saiu do escritório, equipe vazia ou excluída): a regra é pulada. */
  destino_invalido: boolean;
  created_at: string;
  updated_at: string;
}

/** As regras aplicadas, de uma vez, ao acervo que ninguém cuida (retroativo), em segundo plano. */
export interface AplicacaoAoAcervo {
  id: string;
  estado: 'PENDENTE' | 'CONCLUIDO' | 'CANCELADO';
  distribuidos: number;
  /** A regra casaria, mas faltava dado: ficaram de fora. */
  aguardando: number;
  /** Por que parou (CANCELADO). */
  motivo?: string | null;
  created_at: string;
  concluido_em?: string | null;
  /** O gestor desfez a aplicação inteira. */
  desfeito_em?: string | null;
}

export interface PreviaPorRegra {
  regra_id: string;
  nome: string;
  destino: Responsavel;
  /** Os que esta regra leva (é a primeira que casa). */
  vao: number;
  /** Casaria, mas falta dado: ficam como estão. */
  aguardando: number;
  /** O destino saiu: segura os dela sem distribuir. */
  destino_invalido: boolean;
}

export interface PreviaDoAcervo {
  /** Processos sem responsável olhados. */
  olhados: number;
  vao: number;
  regras: PreviaPorRegra[];
  /** Nenhuma regra casa: continuam sem responsável. */
  sem_regra: number;
  parcial: boolean;
  /** Volta no pedido: se alguma regra mudou desde a prévia, o servidor responde 409. */
  versao: string;
  /** Há regra ativa com critério da IA. */
  com_ia?: boolean;
  /** Critério da IA (4b): processos do acervo que a IA ainda precisa avaliar. */
  ia_processos?: number;
  /** Quanto custa avaliá-los, em créditos de IA. */
  ia_creditos?: number;
  /** A avaliação pedida ainda está em curso. */
  ia_avaliando?: boolean;
  /** A última avaliação pedida falhou (nas últimas 24 h): o porquê. */
  ia_falha?: string | null;
}

export interface RegraDistribuicaoIn {
  nome: string;
  ativa: boolean;
  modo: ModoRegra;
  condicoes: CondicaoRegra[];
  destino: Responsavel;
  /** Na alteração: o `updated_at` lido. Se outra tela salvou depois, 409. */
  atualizada_em?: string;
}

export interface PreviaDaRegra {
  fila_casa: number;
  fila_aguardando: number;
  acervo_casa: number;
  acervo_aguardando: number;
  acervo_parcial: boolean;
}

export interface AplicadaPelaRegra {
  processo_id: string;
  numero: string;
  regra_id: string;
  regra_nome: string;
  destino: Responsavel;
  quando: string;
}

export interface DistribuicaoPendente {
  escritorio_id: string;
  nome: string;
  total: number;
}

const base = (escritorioId: string) => `/escritorios/${escritorioId}/regras-distribuicao`;

export async function fetchRegras(escritorioId: string): Promise<RegraDistribuicao[]> {
  const { data } = await api.get<{ regras: RegraDistribuicao[] }>(base(escritorioId));
  return data.regras;
}

export async function criarRegra(escritorioId: string, regra: RegraDistribuicaoIn): Promise<RegraDistribuicao> {
  const { data } = await api.post<RegraDistribuicao>(base(escritorioId), regra);
  return data;
}

export async function alterarRegra(
  escritorioId: string,
  regraId: string,
  regra: RegraDistribuicaoIn,
): Promise<RegraDistribuicao> {
  const { data } = await api.put<RegraDistribuicao>(`${base(escritorioId)}/${regraId}`, regra);
  return data;
}

export async function excluirRegra(escritorioId: string, regraId: string): Promise<void> {
  await api.delete(`${base(escritorioId)}/${regraId}`);
}

export async function reordenarRegras(escritorioId: string, ids: string[]): Promise<RegraDistribuicao[]> {
  const { data } = await api.put<{ regras: RegraDistribuicao[] }>(`${base(escritorioId)}/ordem`, { ids });
  return data.regras;
}

export async function previaDaRegra(escritorioId: string, condicoes: CondicaoRegra[]): Promise<PreviaDaRegra> {
  const { data } = await api.post<PreviaDaRegra>(`${base(escritorioId)}/previa`, { condicoes });
  return data;
}

/** A aplicação ao acervo mais recente (em andamento ou a última); nenhuma, null. */
export async function fetchAplicacaoAoAcervo(escritorioId: string): Promise<AplicacaoAoAcervo | null> {
  const { data } = await api.get<{ lote: AplicacaoAoAcervo | null }>(`${base(escritorioId)}/acervo`);
  return data.lote;
}

export async function previaDoAcervo(escritorioId: string): Promise<PreviaDoAcervo> {
  const { data } = await api.post<PreviaDoAcervo>(`${base(escritorioId)}/acervo/previa`);
  return data;
}

/** Pede a aplicação com a `versao` da prévia que o gestor confirmou. */
export async function aplicarAoAcervo(escritorioId: string, versao: string): Promise<AplicacaoAoAcervo> {
  const { data } = await api.post<AplicacaoAoAcervo>(`${base(escritorioId)}/acervo`, { versao });
  return data;
}

/** Pede a avaliação do acervo pela IA antes de aplicar (gasta créditos; o servidor envia em segundo plano). */
export async function avaliarAcervoComIa(escritorioId: string): Promise<void> {
  await api.post(`${base(escritorioId)}/acervo/avaliar-ia`);
}

export async function pararAplicacaoAoAcervo(escritorioId: string): Promise<AplicacaoAoAcervo> {
  const { data } = await api.post<AplicacaoAoAcervo>(`${base(escritorioId)}/acervo/parar`);
  return data;
}

export async function desfazerAplicacaoAoAcervo(
  escritorioId: string,
  loteId: string,
): Promise<{ lote: AplicacaoAoAcervo; desfeitos: number }> {
  const { data } = await api.post<{ lote: AplicacaoAoAcervo; desfeitos: number }>(
    `${base(escritorioId)}/acervo/${loteId}/desfazer`,
  );
  return data;
}

export async function fetchAplicadas(escritorioId: string): Promise<AplicadaPelaRegra[]> {
  const { data } = await api.get<{ aplicadas: AplicadaPelaRegra[] }>(`${base(escritorioId)}/aplicadas`);
  return data.aplicadas;
}

export async function desfazerAplicadas(escritorioId: string, processoIds: string[]): Promise<number> {
  const { data } = await api.post<{ desfeitos: number }>(`${base(escritorioId)}/aplicadas/desfazer`, {
    processo_ids: processoIds,
  });
  return data.desfeitos;
}

/** Os escritórios em que o usuário distribui com processo na fila (o aviso do painel). */
export async function fetchDistribuicaoPendente(): Promise<DistribuicaoPendente[]> {
  const { data } = await api.get<DistribuicaoPendente[]>('/escritorios/distribuicao/pendentes');
  return data;
}

export interface ItemTpu {
  codigo_cnj: string;
  nome: string;
}

/** Classes ou assuntos da TPU do CNJ, pela busca (`/tabelas`). Só os ativos. */
export async function buscarTpu(tipo: 'classes' | 'assuntos', busca: string): Promise<ItemTpu[]> {
  const { data } = await api.get<{ items: ItemTpu[] }>(`/tabelas/${tipo}`, { params: { search: busca, limit: 30 } });
  return data.items;
}
